// Where library items live on THIS computer. The account library (Eden) is cached here
// too, per account, so the panel opens at once and still works offline.
//
// Items are written in the compact form (compact.ts) and read in either form, so items
// saved by 3.3.x still open. Storage is localStorage (about 5 MB shared with the rest of
// the app), so a refused write is reported, never swallowed, for the user's own items.
import type { LibraryItem } from "./library";
import { decodeItem, encodeItem, type CompactItem } from "./compact";

const LOCAL = "tracecad.library.local";
const accountKey = (accountId: string) => `tracecad.library.account.${accountId}`;

/** Bigger than this and an item is refused up front, with a reason, rather than half stored. */
export const MAX_ITEM_CHARS = 1_500_000;

export interface AccountCacheRow {
  item: LibraryItem;
  edenId: string;
  checksum: string;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false; // storage full or refused: the caller says so
  }
}

const items = (raw: unknown[]): LibraryItem[] => raw.map(decodeItem).filter((i): i is LibraryItem => i !== null);

export function loadLocalItems(): LibraryItem[] {
  return items(read<unknown[]>(LOCAL, []));
}

/** Stored items, still in storage form, so one write does not decode and re-encode the rest. */
function rawLocal(): unknown[] {
  return read<unknown[]>(LOCAL, []);
}

export function saveLocalItem(item: LibraryItem): boolean {
  const kept = rawLocal().filter((r) => (r as { id?: string })?.id !== item.id);
  kept.push(encodeItem(item));
  return write(LOCAL, kept);
}

export function removeLocalItem(id: string): void {
  write(LOCAL, rawLocal().filter((r) => (r as { id?: string })?.id !== id));
}

interface CacheRaw {
  item: CompactItem | LibraryItem;
  edenId: string;
  checksum: string;
}

export function loadAccountCache(accountId: string): AccountCacheRow[] {
  const out: AccountCacheRow[] = [];
  for (const r of read<CacheRaw[]>(accountKey(accountId), [])) {
    const item = r && decodeItem(r.item);
    if (item && typeof r.edenId === "string") out.push({ item, edenId: r.edenId, checksum: String(r.checksum ?? "") });
  }
  return out;
}

/** The cache is a convenience: when it does not fit it is dropped, and Eden is asked again. */
export function saveAccountCache(accountId: string, rows: AccountCacheRow[]): void {
  const raw: CacheRaw[] = rows.map((r) => ({ item: encodeItem(r.item), edenId: r.edenId, checksum: r.checksum }));
  if (!write(accountKey(accountId), raw)) {
    try {
      localStorage.removeItem(accountKey(accountId));
    } catch {
      /* nothing more to do */
    }
  }
}
